import { useEffect, useState } from 'react';
import { reduxForm, SubmissionError } from 'redux-form';
import { useTranslation } from 'react-i18next';

import { user } from 'services';
import { config } from 'app/config';
import { addEpm, updateEpm, getClientsDict } from 'services/payments-service';
import { getCategoriesList, getVehicleClassesList, getOptionsList } from 'services/types';
import { ParkingSession, Confirm, Button, themes, Popup2, Toggle } from 'libs/ui';
import { DictInput, ParkingFilter2, required, WhiteStyledField, Title, ErrorMsg, DatePeriod } from 'libs/form';
import { StyledButtonContainer, RidesWrapperToggle } from './epm-form.styled';

const EpmForm = (props) => {
  const { error, destroy, refresh, setMessage, form, handleSubmit, close, type, clientId, id, meanId, meanType, initialValues, tariffs } = props;
  const [confirm, setConfirm] = useState(false);
  const [data, setData] = useState(null);
  const [checked, setChecked] = useState(initialValues.is_privileged || false);
  const [checkedNeutral, setCheckedNeutral] = useState(initialValues.is_neutral || false);

  const { t } = useTranslation();

  useEffect(() => {
    if (initialValues) {
      setChecked(initialValues.is_privileged);
      setCheckedNeutral(initialValues.is_neutral);
    }
  }, []);

  const rightSessionActive = user.haveRight('csc.show_active_stay');
  const isAdd = type === 'addForm';

  const closeConfirm = () => {
    setConfirm(false);
  };

  /**
   * after submit
   */
  const afterSubmit = () => {
    refresh();
    setMessage(isAdd ? t('messages.Сохранено успешно') : t('messages.Изменения сохранены'));
    setTimeout(() => {
      setMessage('');
    }, 3000);
    close();
    destroy(form);
  };

  /**
   * submit
   * @param {*} values
   * @returns
   */
  const submit = (values) => {
    // data
    const test = false;
    const out = { ...values };
    out.is_blocked = out.is_blocked === 'true' || out.is_blocked === true;
    out.is_privileged = checked === 'true' || checked === true;
    out.is_neutral = checkedNeutral === 'true' || checkedNeutral === true;
    out.vehicle_category = out.vehicle_category ? parseInt(out.vehicle_category) : null;
    out.vehicle_class = out.vehicle_class ? parseInt(out.vehicle_class) : null;
    if (!out.client_id && clientId) {
      out.client_id = clientId;
    }
    if (!clientId && !out.client_id) {
      out.client_id = null;
      out.client_name = null;
    }

    if (!isAdd) {
      out.id = id;
      // confirm
      if (out.mean_id !== props.initialValues.mean_id) {
        setData(out);
        setConfirm(true);
        return false;
      }
    }
    if (test) {
      console.log('🚀 ~ submit ~ out:', out);
      return Promise.resolve();
    }

    // method
    const method = isAdd ? addEpm : updateEpm;
    return method(out)
      .then(() => {
        afterSubmit();
      })
      .catch((errors) => {
        if (errors && errors.data) {
          throw new SubmissionError({
            _error: errors.data.type,
          });
        }
        if (errors && errors.field) {
          throw new SubmissionError({
            [errors.field]: errors.message,
          });
        }
      });
  };

  const submitAfterConfirm = () => {
    return updateEpm(data)
      .then(() => {
        afterSubmit();
      })
      .catch((errors) => {
        if (errors && errors.data) {
          throw new SubmissionError({
            _error: errors.data.type,
          });
        }
      });
  };

  const cancel = () => {
    close();
    destroy(form);
  };

  const getClients = (name) => {
    return getClientsDict({
      filter_params: { name },
      sort_params: [{ field_name: 'name', direction: 'asc' }],
      paging_params: { limit: 5, offset: 0 },
    });
  };

  const title = isAdd ? t('form.Добавить БСК') : t('form.Изменить БСК');

  const onChangeToggle = (event) => {
    const value = event.target.checked;
    setChecked(value);
  };

  const onChangeToggleNeutralStatus = (event) => {
    const value = event.target.checked;
    setCheckedNeutral(value);
  };

  return (
    <Popup2 w={482} top={3}>
      <Title>{title}</Title>
      <form onSubmit={handleSubmit(submit)}>
        <ErrorMsg error={error} />
        <ParkingFilter2 />

        {!clientId && (
          <WhiteStyledField
            selectedName={initialValues.client_name}
            label={t('form.Клиент')}
            placeholder={t('form.Клиент')}
            type="autocomplete2"
            name="client_id"
            getList={getClients}
          />
        )}

        <DictInput dict="payment_mean.status" />

        <WhiteStyledField type="text" label={t('sections.ГРЗ')} placeholder={t('form.А000AA 000 rus')} name="vehicle_plate" />

        <WhiteStyledField type="text" label={t('sections.БСК') + config.req} placeholder="3786 4678 6467 5656" name="mean_id" validate={required(t)} />

        <WhiteStyledField type="text" label={t('form.Держатель') + config.req} placeholder="Введите текст" name="owner" validate={required(t)} />

        <WhiteStyledField type="select" label={t('dict_filter.Класс ТС')} name="vehicle_class">
          <option value={''}>{t('form.Не задано')}</option>
          {getVehicleClassesList()}
        </WhiteStyledField>

        <WhiteStyledField type="select" label={t('form.Категория')} name="vehicle_category">
          <option value={''}>{t('form.Не задано')}</option>
          {getCategoriesList()}
        </WhiteStyledField>

        <DatePeriod />

        <WhiteStyledField className="shortfield" type="select" label={t('form.Тариф')} name="tariff_id">
          <option value="">{t('form.Не задано')}</option>
          {getOptionsList(tariffs)}
        </WhiteStyledField>

        <RidesWrapperToggle>
          <Toggle name="is_privileged" value={checked || false} label={t('form.Свободный проезд')} onChange={onChangeToggle} checked={checked || false} />
        </RidesWrapperToggle>

        <RidesWrapperToggle>
          <Toggle
            name="is_neutral"
            value={checkedNeutral || false}
            label={t('form.Нейтральное положение автомобиля')}
            onChange={onChangeToggleNeutralStatus}
            checked={checkedNeutral || false}
          />
        </RidesWrapperToggle>

        {!isAdd && rightSessionActive && <ParkingSession meanType={meanType} meanId={meanId} />}

        <StyledButtonContainer space="20px">
          <Button type="button" onClick={cancel}>
            {t('buttons.Отменить')}
          </Button>
          <Button theme={themes.blue}>
            <i className="fa fa-save" />
            {t('buttons.Сохранить')}
          </Button>
        </StyledButtonContainer>

        {confirm && (
          <Confirm
            width={'550px'}
            title={t('form.Номер карты для данной записи будет изменён. Предыдущий номер карты будет удалён. Продолжить?')}
            type="danger"
            cancelButton={{ text: t('buttons.Отмена'), func: closeConfirm }}
            acceptButton={{ text: t('form.Изменить номер карты'), func: submitAfterConfirm, theme: themes.red }}
          />
        )}
      </form>
    </Popup2>
  );
};

//vehicle_category: props.categories ? props.categories[0].id : null,

export default reduxForm({
  form: 'clientEpmForm',
  destroyOnUnmount: false,
})(EpmForm);
